import { useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';

// Animation presets for common effects
export const animationPresets = {
  fadeIn: {
    opacity: 0,
    duration: 0.6,
    ease: 'power2.out',
  },
  slideInUp: {
    y: 50,
    opacity: 0,
    duration: 0.6,
    ease: 'power2.out',
  },
  slideInLeft: {
    x: -50,
    opacity: 0,
    duration: 0.6,
    ease: 'power2.out',
  },
  slideInRight: {
    x: 50,
    opacity: 0,
    duration: 0.6,
    ease: 'power2.out',
  },
  scaleIn: {
    scale: 0.8,
    opacity: 0,
    duration: 0.5,
    ease: 'back.out(1.7)',
  },
  bounceIn: {
    scale: 0.3,
    opacity: 0,
    duration: 0.6,
    ease: 'back.out(1.7)',
  },
  rotateIn: {
    rotation: -180,
    opacity: 0,
    duration: 0.6,
    ease: 'power2.out',
  },
};

// Custom hook for GSAP animations
export const useGSAP = () => {
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (timelineRef.current) {
      timelineRef.current.kill();
      timelineRef.current = null;
    }
  }, []);

  // Create a new timeline
  const createTimeline = useCallback(() => {
    cleanup();
    timelineRef.current = gsap.timeline();
    return timelineRef.current;
  }, [cleanup]);

  // Fade in animation
  const fadeIn = useCallback(
    (element: HTMLElement | string, options = {}) => {
      const timeline = createTimeline();
      const config = { ...animationPresets.fadeIn, ...options };

      timeline.fromTo(
        element,
        { opacity: config.opacity },
        { opacity: 1, duration: config.duration, ease: config.ease }
      );

      return timeline;
    },
    [createTimeline]
  );

  // Slide in animation
  const slideIn = useCallback(
    (
      element: HTMLElement | string,
      direction: 'up' | 'down' | 'left' | 'right' = 'up',
      options = {}
    ) => {
      const timeline = createTimeline();
      const preset =
        direction === 'up'
          ? animationPresets.slideInUp
          : direction === 'down'
            ? { ...animationPresets.slideInUp, y: -50 }
            : direction === 'left'
              ? animationPresets.slideInLeft
              : animationPresets.slideInRight;

      const config = { ...preset, ...options } as {
        y?: number;
        x?: number;
        opacity?: number;
        duration?: number;
        ease?: string;
      };

      timeline.fromTo(
        element,
        {
          opacity: config.opacity,
          ...(typeof config.y === 'number' ? { y: config.y } : {}),
          ...(typeof config.x === 'number' ? { x: config.x } : {}),
        },
        { y: 0, x: 0, opacity: 1, duration: config.duration, ease: config.ease }
      );

      return timeline;
    },
    [createTimeline]
  );

  // Scale in animation
  const scaleIn = useCallback(
    (element: HTMLElement | string, options = {}) => {
      const timeline = createTimeline();
      const config = { ...animationPresets.scaleIn, ...options };

      timeline.fromTo(
        element,
        { scale: config.scale, opacity: config.opacity },
        { scale: 1, opacity: 1, duration: config.duration, ease: config.ease }
      );

      return timeline;
    },
    [createTimeline]
  );

  // Rotate in animation
  const rotateIn = useCallback(
    (element: HTMLElement | string, options = {}) => {
      const timeline = createTimeline();
      const config = { ...animationPresets.rotateIn, ...options };

      timeline.fromTo(
        element,
        { rotation: config.rotation, opacity: config.opacity },
        {
          rotation: 0,
          opacity: 1,
          duration: config.duration,
          ease: config.ease,
        }
      );

      return timeline;
    },
    [createTimeline]
  );

  // Stagger animation for lists
  const staggerIn = useCallback(
    (
      elements: HTMLElement[] | string,
      animation: 'fade' | 'slide' | 'scale' = 'fade',
      options: { stagger?: number } = {}
    ) => {
      const timeline = createTimeline();
      const staggerDelay = options.stagger ?? 0.1;

      switch (animation) {
        case 'fade':
          timeline.fromTo(
            elements,
            { opacity: 0 },
            {
              opacity: 1,
              duration: 0.4,
              stagger: staggerDelay,
              ease: 'power2.out',
            }
          );
          break;
        case 'slide':
          timeline.fromTo(
            elements,
            { y: 30, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.5,
              stagger: staggerDelay,
              ease: 'power2.out',
            }
          );
          break;
        case 'scale':
          timeline.fromTo(
            elements,
            { scale: 0.8, opacity: 0 },
            {
              scale: 1,
              opacity: 1,
              duration: 0.4,
              stagger: staggerDelay,
              ease: 'back.out(1.7)',
            }
          );
          break;
      }

      return timeline;
    },
    [createTimeline]
  );

  // Page transition animation
  const pageTransition = useCallback(
    (element: HTMLElement | string, options = {}) => {
      const timeline = createTimeline();
      const config = {
        duration: 0.8,
        ease: 'power2.inOut',
        ...options,
      };

      timeline.set(element, { opacity: 0, scale: 0.95 }).to(element, {
        opacity: 1,
        scale: 1,
        duration: config.duration,
        ease: config.ease,
      });

      return timeline;
    },
    [createTimeline]
  );

  // Button hover animation
  const buttonHover = useCallback(
    (element: HTMLElement | string, options = {}) => {
      const config = {
        scale: 1.05,
        duration: 0.2,
        ease: 'power2.out',
        ...options,
      };

      return gsap.to(element, config);
    },
    []
  );

  // Button click animation
  const buttonClick = useCallback(
    (element: HTMLElement | string, options = {}) => {
      const config = {
        scale: 0.95,
        duration: 0.1,
        ease: 'power2.out',
        ...options,
      };

      return gsap.to(element, config);
    },
    []
  );

  // Scratch card reveal animation
  const scratchReveal = useCallback(
    (element: HTMLElement | string, options = {}) => {
      const timeline = createTimeline();
      const config = {
        duration: 0.8,
        ease: 'power2.out',
        ...options,
      };

      timeline
        .set(element, { scale: 0.8, opacity: 0, rotation: -5 })
        .to(element, {
          scale: 1,
          opacity: 1,
          rotation: 0,
          duration: config.duration,
          ease: config.ease,
        })
        .to(
          element,
          { scale: 1.05, duration: 0.2, ease: 'power2.out' },
          '-=0.1'
        )
        .to(element, { scale: 1, duration: 0.2, ease: 'power2.out' });

      return timeline;
    },
    [createTimeline]
  );

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    fadeIn,
    slideIn,
    scaleIn,
    rotateIn,
    staggerIn,
    pageTransition,
    buttonHover,
    buttonClick,
    scratchReveal,
    createTimeline,
    cleanup,
  };
};

export default useGSAP;
